// frontend/src/components/ModernCarousel.js
// Modern CSS-native Carousel using Chrome 135+ features
// Based on: https://developer.chrome.com/blog/new-in-web-ui-io-2025-recap?hl=de

import React, { useRef, useState, useEffect } from 'react';

/**
 * Modern Carousel Component using CSS ::scroll-button and ::scroll-marker
 * This component leverages the latest CSS scroll features for a native,
 * accessible carousel experience without complex JavaScript.
 * The styling is provided by the .md3-carousel class in index.css
 */
const ModernCarousel = ({ children, className = "", enableMarkers = true, variableWidth = false, animated = true }) => {
    // Convert children to array and add data attributes for accessibility
    const items = React.Children.toArray(children);
    const scrollContainerRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(true);

    useEffect(() => {
        const checkScroll = () => {
            if (scrollContainerRef.current) {
                const { scrollWidth, clientWidth } = scrollContainerRef.current;
                // Add a small buffer (1px) to avoid precision issues
                setIsScrollable(scrollWidth > clientWidth + 1);
            }
        };

        // Initial check and on resize
        checkScroll();
        window.addEventListener('resize', checkScroll);

        // Also check after a short delay to ensure layout, and mutation observer could be better but simple delay helps
        const timer = setTimeout(checkScroll, 100);

        return () => {
            window.removeEventListener('resize', checkScroll);
            clearTimeout(timer);
        };
    }, [children]); // Re-check when children change

    return (
        <div className={`md3-carousel-wrapper ${className} ${!isScrollable ? 'no-scroll' : ''}`}>
            <ul
                ref={scrollContainerRef}
                className={`md3-carousel ${variableWidth ? 'variable-width' : ''}`}
            >
                {items.map((child, index) => (
                    <li
                        key={index}
                        className={`md3-carousel-item ${animated ? 'animated' : ''}`}
                        data-name={`Item ${index + 1}`}
                        aria-label={`Item ${index + 1} of ${items.length}`}
                    >
                        {child}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ModernCarousel;
